/**
 * Card Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 * Siehe: docs/frontend/design-rules.md (Tokens: docs/frontend/design-contract.md)
 */

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '@/lib/utils';

// ============================================================================
// Card Root
// ============================================================================

export type CardProps = ComponentPropsWithoutRef<'div'>;

/**
 * Card Component
 *
 * Features:
 * - WCAG 2.1 AA compliant (3:1 border contrast)
 * - Dark/Light mode support
 * - Semantic HTML structure
 * - Composable with CardHeader, CardContent, CardFooter
 *
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Produkt erstellen</CardTitle>
 *     <CardDescription>
 *       Erstellen Sie ein neues Produkt
 *     </CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     <Input label="Name" />
 *   </CardContent>
 *   <CardFooter>
 *     <Button>Speichern</Button>
 *   </CardFooter>
 * </Card>
 * ```
 */
export const Card = forwardRef<ElementRef<'div'>, CardProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg border',
          // Light/Dark mode backgrounds
          'bg-white dark:bg-neutral-900',
          'text-neutral-900 dark:text-neutral-50',
          'shadow-sm',
          // WCAG 2.1: 3:1 contrast for borders
          'border-neutral-200 dark:border-neutral-800',
          className
        )}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// ============================================================================
// Card Header
// ============================================================================

export type CardHeaderProps = ComponentPropsWithoutRef<'div'>;

export const CardHeader = forwardRef<ElementRef<'div'>, CardHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-1.5 p-6', className)}
        {...props}
      />
    );
  }
);

CardHeader.displayName = 'CardHeader';

// ============================================================================
// Card Title
// ============================================================================

export type CardTitleProps = ComponentPropsWithoutRef<'h3'>;

export const CardTitle = forwardRef<ElementRef<'h3'>, CardTitleProps>(
  ({ className, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={cn(
          'text-2xl font-semibold leading-none tracking-tight',
          'text-neutral-900 dark:text-neutral-50',
          className
        )}
        {...props}
      />
    );
  }
);

CardTitle.displayName = 'CardTitle';

// ============================================================================
// Card Description
// ============================================================================

export type CardDescriptionProps = ComponentPropsWithoutRef<'p'>;

export const CardDescription = forwardRef<ElementRef<'p'>, CardDescriptionProps>(
  ({ className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn(
          'text-sm',
          // WCAG AA: 4.5:1 contrast
          'text-neutral-600 dark:text-neutral-400',
          className
        )}
        {...props}
      />
    );
  }
);

CardDescription.displayName = 'CardDescription';

// ============================================================================
// Card Content
// ============================================================================

export type CardContentProps = ComponentPropsWithoutRef<'div'>;

export const CardContent = forwardRef<ElementRef<'div'>, CardContentProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />;
  }
);

CardContent.displayName = 'CardContent';

// ============================================================================
// Card Footer
// ============================================================================

export type CardFooterProps = ComponentPropsWithoutRef<'div'>;

export const CardFooter = forwardRef<ElementRef<'div'>, CardFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-2 p-6 pt-0', className)}
        {...props}
      />
    );
  }
);

CardFooter.displayName = 'CardFooter';
